import { Platform, TimeInterval, TimeRange } from '@fullstory/proto/fs/api/segment';
import { Metric } from '@fullstory/proto/fs/services/search-settings';
import { BuiltInDimension, Measure, SortType } from '@fullstory/proto/fs/api/metrics';
import {
  DEFAULT_CONSTRAINTS,
  DEFAULT_SINGLE_NUMBER_VISUALIZATION_SETTINGS,
  DEFAULT_TIME_RANGE,
  useMetricComputations,
} from '../';
import { Scope } from '@fullstory/proto/fs/api/search';
import { SortOrder } from '@fullstory/proto/fs/services/alexa';
import { useMemo } from 'react';
import { DataTableWithId } from '@fullstory/redux';
import { useFlags } from '@fullstory/react-hooks';
import { Flag } from '@fullstory/proto/fs/shared/flags';

const pageIdFromMetricComputation = (results: DataTableWithId[]): number | undefined => {
  const indexPageIdFromMetric = (index: number) => {
    const group = results[0]?.table[index]?.groups[0];
    return group ? Number(group.groupId) : undefined;
  };
  // The first result might be "Not defined", so in this case we pick the second most visited
  const maybeTopPageId = indexPageIdFromMetric(0);
  return maybeTopPageId == 0 ? indexPageIdFromMetric(1) : maybeTopPageId;
};

/** Returns the ID of the 'top web page' for the org in context. If the top page is 'Not defined'
 * it will return the second top page. A top web page is the page with most views in the org.
 * @param timeRange The time range to use for the metric search, defaults to past 7 days.
 *
 * TODO(tylermorton) This does not filter out archived pages
 */
export const useTopWebPageId = (timeRange: TimeRange = DEFAULT_TIME_RANGE): number | undefined => {
  const metric: Metric = useMemo(
    () => ({
      id: '',
      name: '',
      description: '',
      metric: {
        single: {
          event: {
            primary: {
              visitedPage: {
                any: {},
              },
            },
            deps: [],
          },
          aggregation: {
            default: {},
          },
          rollingWindow: 0,
          rollingWindowType: Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID,
          rollingWindowInterval: TimeInterval.TIME_INTERVAL_INVALID,
        },
      },
      dimensionality: {
        ...DEFAULT_SINGLE_NUMBER_VISUALIZATION_SETTINGS(true),
        segment: {
          segmentScope: Scope.SCOPE_SESSION,
          segmentDefinition: {
            userProperties: [],
            excludeUserProperties: [],
            behaviors: [],
            excludeBehaviors: [],
            constraints: {
              ...DEFAULT_CONSTRAINTS,
              timeRange,
              platform: Platform.PLATFORM_WEB,
            },
            excludeConstraints: null,
          },
        },
        timeRange,
        dimensionSettings: {
          sortOrder: SortOrder.SORT_ORDER_DESC,
          sortByDefault: false,
          sortType: SortType.SORT_TYPE_INVALID,
          // We only want the top page, but we request the top two in case the first one is "Not defined"
          limit: 2,
          computeTopLevel: true,
          computeNumGroups: false,
          allowAllPartialResults: false,
          noFallback: false,
          dimensions: [
            {
              builtInProperty: BuiltInDimension.DIMENSION_PAGE_DEF_ID,
              prefix: '',
              limit: 0,
              computeAtLevel: false,
              eventRefinement: null,
            },
          ],
          dimension: {
            builtInProperty: BuiltInDimension.DIMENSION_PAGE_DEF_ID,
            prefix: '',
            limit: 0,
            computeAtLevel: false,
            eventRefinement: null,
          },
        },
      },
      funnelFilter: null,
    }),
    [timeRange],
  );
  const { results } = useMetricComputations(metric, true, 'top-web-page-past-week', false);
  return useMemo(() => pageIdFromMetricComputation(results), [results]);
};

/** Returns the ID of the 'top mobile page' for the org in context. If the top page is 'Not defined'
 * it will return the second top page. A top mobile page is the page with most views in the org.
 * @param timeRange The time range to use for the metric search, defaults to past 7 days.
 *
 *   TODO(tylermorton) This does not filter out archived pages
 */
export const useTopMobilePageId = (
  timeRange: TimeRange = DEFAULT_TIME_RANGE,
): number | undefined => {
  const metric: Metric = useMemo(
    () => ({
      id: '',
      name: '',
      description: '',
      metric: {
        single: {
          event: {
            primary: {
              visitedPage: {
                any: {},
              },
            },
            deps: [],
          },
          aggregation: {
            default: {},
          },
          rollingWindow: 0,
          rollingWindowType: Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID,
          rollingWindowInterval: TimeInterval.TIME_INTERVAL_INVALID,
        },
      },
      dimensionality: {
        ...DEFAULT_SINGLE_NUMBER_VISUALIZATION_SETTINGS(true),
        segment: {
          segmentScope: Scope.SCOPE_SESSION,
          segmentDefinition: {
            userProperties: [],
            excludeUserProperties: [],
            behaviors: [],
            excludeBehaviors: [],
            constraints: {
              ...DEFAULT_CONSTRAINTS,
              timeRange,
              platform: Platform.PLATFORM_NATIVEMOBILE,
            },
            excludeConstraints: null,
          },
        },
        timeRange,
        dimensionSettings: {
          sortOrder: SortOrder.SORT_ORDER_DESC,
          sortByDefault: false,
          sortType: SortType.SORT_TYPE_INVALID,
          // We only want the top page, but we request the top two in case the first one is "Not defined"
          limit: 2,
          computeTopLevel: true,
          computeNumGroups: false,
          allowAllPartialResults: false,
          noFallback: false,
          dimensions: [
            {
              builtInProperty: BuiltInDimension.DIMENSION_PAGE_DEF_ID,
              prefix: '',
              limit: 0,
              computeAtLevel: false,
              eventRefinement: null,
            },
          ],
          dimension: {
            builtInProperty: BuiltInDimension.DIMENSION_PAGE_DEF_ID,
            prefix: '',
            limit: 0,
            computeAtLevel: false,
            eventRefinement: null,
          },
        },
      },
      funnelFilter: null,
    }),
    [timeRange],
  );
  const { results } = useMetricComputations(metric, true, 'top-mobile-page-past-week', false);
  return useMemo(() => pageIdFromMetricComputation(results), [results]);
};

/** Returns the ID of the 'top page' for the org in context. If the top page is 'Not defined'
 * it will return the second top page. A top page is the page with most views in the org.
 * @param timeRange The time range to use for the metric search, defaults to past 7 days.
 */
export const useTopPageMetric = (timeRange: TimeRange = DEFAULT_TIME_RANGE): number | undefined => {
  const flags = useFlags();
  const topWebPageId = useTopWebPageId(timeRange);
  const topMobilePageId = useTopMobilePageId(timeRange);
  if (flags.hasBit(Flag.FeatNativeMobile)) {
    // if a NM enabled org also has a top web page, it should take priority
    return topWebPageId !== undefined ? topWebPageId : topMobilePageId;
  } else {
    return topWebPageId;
  }
};
